/**
 * Conteúdo da tela Info / Updates.
 *
 * COMO ATUALIZAR: entrada nova no TOPO de `CHANGELOG`; se a mudança cria ou remove capacidade,
 * ajustar `FEATURES` também; e mexer em `PLATFORM_UPDATED`. Único arquivo a editar.
 *
 * `onde` é a rota real do app, conferida contra `routes/router.tsx`. Item sem rota é capacidade de
 * plataforma, não tela.
 */

export const PLATFORM_VERSION = '1.0';
export const PLATFORM_UPDATED = '2026-08-07';

export interface Feature {
  nome: string;
  /** Rota do app, quando a funcionalidade tem tela própria. */
  onde?: string;
  detalhe?: string;
  /** Papéis que acessam, quando a tela é restrita. Ausente = todos. Espelha o menu. */
  papeis?: string[];
}

export const FEATURES: { area: string; items: Feature[] }[] = [
  {
    area: 'Rastreamento e mapa',
    items: [
      { nome: 'Frota consolidada', onde: '/devices', detalhe: 'cartões por embarcação com status, telemetria resumida e ações rápidas' },
      { nome: 'Mapa náutico ao vivo', onde: '/map', detalhe: 'posição, trilha e status atualizados por WebSocket' },
      { nome: 'Painel', onde: '/dashboard', detalhe: 'visão geral da frota, alertas recentes e indicadores' },
      { nome: 'Detalhe da embarcação', onde: '/devices/:id', detalhe: 'ficha, posição atual, histórico e comandos' },
      { nome: 'Saúde da embarcação', onde: '/devices/:id/health', detalhe: 'energia, casco e porão, motor' },
      { nome: 'Comandos ao rastreador', detalhe: 'bloqueio e desbloqueio de motor, posição sob demanda, reinício' },
      { nome: 'Protocolos suportados', detalhe: 'GT06, Suntech, Teltonika, Coban, H02, Meitrack, Ruptela e os demais do Traccar' },
    ],
  },
  {
    area: 'Náutico',
    items: [
      { nome: 'Condições do mar', onde: '/conditions', detalhe: 'vento, ondas, visibilidade, maré e fase lunar (CPTEC/INMET/Open-Meteo)' },
      { nome: 'Meus Pontos', onde: '/points', detalhe: 'pontos do navegador com distância, importação e exportação' },
      { nome: 'Navegador Premium', onde: '/navigator', detalhe: 'navegação pelo GPS do celular, com rumo e distância até o destino' },
      { nome: 'Rotas do app', onde: '/app-routes', detalhe: 'rotas salvas e replay' },
      { nome: 'Geocoder náutico', detalhe: 'referência de local em vez de endereço urbano; nunca devolve rua na água' },
      { nome: 'Carta náutica', onde: '/carta/', detalhe: 'carta estática com embarcações e marcação náutica' },
    ],
  },
  {
    area: 'Alertas e notificações',
    items: [
      { nome: 'Alertas', onde: '/notifications', detalhe: 'lista por embarcação, isolada por usuário' },
      { nome: 'Regras de notificação', onde: '/notifications/rules', detalhe: 'evento, alcance, destinatários, filtro de alarme e canais' },
      { nome: 'Alarmes personalizados', onde: '/notifications/custom', detalhe: 'qualquer entrada ou sensor do rastreador vira alarme com nome próprio', papeis: ['admin'] },
      { nome: 'Diagnóstico de notificação', detalhe: 'mostra em que elo a corrente arrebentou: posição, condição, evento e regra' },
      { nome: 'Canais de entrega', detalhe: 'painel web, push no app (FCM nativo), e-mail e Telegram' },
      { nome: 'Alarmes de segurança', detalhe: 'SOS, água no porão, corte de energia, reboque, jammer, violação, remoção, antena cortada, impacto e vazamento' },
      { nome: 'Envio manual de mensagem', detalhe: 'push direto para usuários escolhidos' },
    ],
  },
  {
    area: 'Operação',
    items: [
      { nome: 'Geocercas', onde: '/geofences', detalhe: 'marina, fundeio e raio de âncora, com alerta de entrada e saída' },
      { nome: 'Central 24h', onde: '/central', detalhe: 'painel de acompanhamento contínuo' },
      { nome: 'Manutenção', onde: '/maintenance', detalhe: 'preventiva por horas de motor, dias ou meses, com aviso de vencimento' },
      { nome: 'Checklist', onde: '/checklist', detalhe: 'vistoria registrada por embarcação' },
      { nome: 'Locadora', onde: '/rentals', detalhe: 'controle de locação e retorno' },
      { nome: 'Modos de operação', detalhe: 'Normal, Segurança, Manutenção, Transporte e Locação' },
      { nome: 'Auto-bloqueio por geocerca', detalhe: 'bloqueia o motor ao entrar ou sair da área definida' },
    ],
  },
  {
    area: 'Relatórios',
    items: [
      { nome: 'Central de relatórios', onde: '/reports' },
      { nome: 'Eventos', onde: '/reports/events' },
      { nome: 'Resumo', onde: '/reports/summary' },
      { nome: 'Viagens', onde: '/reports/trips' },
      { nome: 'Paradas', onde: '/reports/stops' },
      { nome: 'Rota', onde: '/reports/route' },
      { nome: 'Velocidade', onde: '/reports/speed' },
      { nome: 'Horímetro', onde: '/reports/hours' },
      { nome: 'Seguro', onde: '/reports/insurance' },
      { nome: 'Uso', onde: '/reports/usage' },
      { nome: 'Eventos de geocerca', onde: '/reports/geofence-events' },
    ],
  },
  {
    area: 'Cadastros e configuração',
    items: [
      { nome: 'Configurações', onde: '/settings' },
      { nome: 'Cadastro de embarcação', onde: '/settings/devices/new', detalhe: 'edição em /settings/devices/edit/:id' },
      { nome: 'Clientes', onde: '/settings/clients', detalhe: 'grupos de embarcações' },
      { nome: 'Usuários e vínculos', onde: '/users', detalhe: 'quem enxerga qual embarcação' },
      { nome: 'Links úteis', onde: '/settings/links' },
      { nome: 'Perfil', onde: '/profile', detalhe: 'preferências, som de alerta e sessão' },
    ],
  },
  {
    area: 'Administração e revendas',
    items: [
      { nome: 'Revendas', onde: '/admin/tenants', detalhe: 'módulos, cotas de dispositivo e usuário, white-label', papeis: ['admin'] },
      { nome: 'Operadores', onde: '/admin/operators', detalhe: 'papéis administrativos', papeis: ['admin'] },
      { nome: 'Uso de features', onde: '/admin/feature-usage', detalhe: 'quem liberou feature paga, por revenda', papeis: ['admin'] },
      { nome: 'Info / Updates', onde: '/admin/info', detalhe: 'esta tela', papeis: ['admin', 'revenda'] },
      { nome: 'Manuais', onde: '/manuais', detalhe: 'documentação de uso da plataforma' },
      { nome: 'Clientes da revenda', onde: '/revenda/clientes', papeis: ['revenda'] },
      { nome: 'Papéis e permissões', detalhe: 'admin, revenda, operadores e cliente final, com restrição de cadastro e vínculo' },
    ],
  },
  {
    area: 'Financeiro e cobrança',
    items: [
      { nome: 'Cobrança', onde: '/billing', detalhe: 'faturas por dispositivo', papeis: ['admin', 'revenda', 'operador_financeiro', 'cliente'] },
      { nome: 'Financeiro', onde: '/financeiro', detalhe: 'visão de admin e de revenda', papeis: ['admin', 'revenda', 'operador_financeiro'] },
      { nome: 'Indicadores', onde: '/bi' },
      { nome: 'Gateways de pagamento', detalhe: 'Asaas, Mercado Pago e manual' },
    ],
  },
  {
    area: 'Conectividade e campo',
    items: [
      { nome: 'Chips / SIM', onde: '/chips', detalhe: 'inventário, status e consumo', papeis: ['admin', 'revenda', 'operador_suporte'] },
      { nome: 'Ativação técnica', onde: '/install', detalhe: 'roteiro de instalação e conferência em campo', papeis: ['admin', 'operador_suporte', 'tecnico'] },
    ],
  },
  {
    area: 'Aplicativo mobile',
    items: [
      { nome: 'App do proprietário e operador', detalhe: 'React Native e Expo: frota, mapa, alertas, comandos, navegador, pontos e modo de segurança' },
      { nome: 'Push no celular', detalhe: 'FCM nativo; iOS depende da chave APNs no projeto Firebase' },
    ],
  },
];

/** Últimos updates, mais recentes no topo. */
export const CHANGELOG: { date: string; title: string; items: string[] }[] = [
  {
    date: '2026-08-07',
    title: 'Alarmes personalizados, diagnóstico de notificação, Manuais e Info completo',
    items: [
      'Nova tela "Alarmes Personalizados" (admin): qualquer informação que o rastreador envia, como uma entrada digital, uma boia de porão ou uma leitura de motor, vira alarme com nome próprio, que entra no histórico, aparece em relatório e dispara aviso.',
      'Construtor de condição com oito operadores e combinação por E / OU, mais "Descobrir atributos do veículo", que lê as últimas posições e mostra o que aquele equipamento realmente manda.',
      'Teste antes de salvar: avalia a condição contra a última posição e diz se ela está satisfeita agora.',
      'Avisos de falha silenciosa: destinatário sem app registrado ou sem Telegram vinculado, nome igual a um alarme de fábrica, e o efeito de substituição enquanto a condição estiver ativa.',
      'Novo "Diagnóstico" nas duas telas de notificação: mostra a cadeia inteira de uma embarcação e diz qual elo não é observável pela tela.',
      'Nova área "Manuais", com o primeiro manual publicado (alarmes personalizados).',
      'Info / Updates passou a listar todas as telas e capacidades da plataforma, com o caminho de cada uma.',
    ],
  },
  {
    date: '2026-08-05',
    title: 'Permissões, isolamento de notificações, alarmes e cobrança de features',
    items: [
      'Cliente final não cadastra usuários, dispositivos nem faz vínculos; mantém notificações e geocercas.',
      'Gestão de Chips/SIM e Cobrança desligadas por padrão para o usuário final.',
      'Notificações isoladas por usuário e por dispositivo (sem vazamento entre contas no mesmo navegador).',
      'Novos alarmes de segurança: jammer, violação, remoção, antena GPS cortada, impacto e vazamento, com nomes em português.',
      'Nova tela "Uso de Features": conta, por revenda, quem liberou o Navegador Premium (base para cobrança).',
      'Correções: revenda voltou a cadastrar usuários e embarcações (cota de dispositivos sincronizada com o rastreador); mensagem de erro real ao cadastrar; localização de idioma do push mobile reincorporada.',
    ],
  },
  {
    date: '2026-07-30',
    title: 'Site institucional e mapa de arquitetura',
    items: [
      'Site nautmax.com.br com posicionamento para todas as águas (mar, rios, lagos, represas, orla) e responsividade.',
      'Documentação de arquitetura atualizada para o push nativo do Traccar (projeto Firebase nautmax-bb688).',
    ],
  },
];
